export interface PaginatedResult<T> {
  items: T[];
  total: number;
}

export interface FetchAllPagesOptions {
  pageSize?: number;
  signal?: AbortSignal;
}

export async function fetchAllPages<T>(
  fetchPage: (
    page: number,
    pageSize: number,
  ) => Promise<PaginatedResult<T> | undefined>,
  options: FetchAllPagesOptions = {},
): Promise<T[]> {
  const { pageSize = 100, signal } = options;

  if (signal?.aborted) {
    throw signal.reason ?? new DOMException('Signal aborted', 'AbortError');
  }

  const firstResult = await fetchPage(1, pageSize);

  if (!firstResult) {
    return [];
  }

  const allItems = [...firstResult.items];
  const totalPages = Math.ceil(firstResult.total / pageSize);

  if (totalPages <= 1) {
    return allItems;
  }

  if (signal?.aborted) {
    throw signal.reason ?? new DOMException('Signal aborted', 'AbortError');
  }

  const results = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      fetchPage(i + 2, pageSize),
    ),
  );

  for (const result of results) {
    if (result) {
      allItems.push(...result.items);
    }
  }

  return allItems;
}
